import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Trash2, Info, Image as ImageIcon, RefreshCw, ExternalLink } from "lucide-react";
import { invalidateImageSlots } from "@/components/ImageSlot";

const NAVY = "#0E1D2D";
const GOLD = "#C9953D";
const CREAM = "#F4F2EF";
const BORDER = "#E7E5E2";

export default function AdminImages() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);      // slot_key sendo processado
  const [bust, setBust] = useState(0);         // cache-buster nas <img>

  const load = () =>
    api.get("/admin/image-slots")
      .then((r) => setItems(r.data?.items || []))
      .catch(() => toast.error("Falha ao carregar slots"))
      .finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  async function handleUpload(slotKey, file) {
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) return toast.error("Arquivo > 20 MB");
    setBusy(slotKey);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/admin/image-slots/${slotKey}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Imagem publicada");
      invalidateImageSlots();
      setBust((n) => n + 1);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha no upload");
    } finally {
      setBusy(null);
    }
  }

  async function handleRemove(slotKey) {
    if (!confirm("Remover imagem customizada deste slot?")) return;
    setBusy(slotKey);
    try {
      await api.delete(`/admin/image-slots/${slotKey}`);
      toast.success("Removida — voltando à imagem padrão");
      invalidateImageSlots();
      setBust((n) => n + 1);
      await load();
    } catch {
      toast.error("Falha ao remover");
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="min-h-screen p-3 sm:p-6 lg:p-8 font-sans" style={{ background: "#FAF9F6", color: "#132238" }} data-testid="admin-images">
      <header className="mb-8">
        <div className="text-[11px] uppercase tracking-[0.25em]" style={{ color: GOLD }}>ADMIN · IDENTIDADE VISUAL</div>
        <h1 className="font-serif text-4xl mt-1">Imagens do Hermes</h1>
        <p className="text-sm text-slate-600 mt-2 max-w-2xl">
          Cada slot abaixo corresponde a um ponto <strong>estratégico</strong> da interface do Hermes. Envie sua
          imagem em 4K (JPG/PNG/WEBP até 20 MB) — ela substituirá o placeholder padrão em todos os locais
          identificados. Você pode reverter para o padrão a qualquer momento.
        </p>
      </header>

      {loading ? (
        <div className="text-slate-400 text-sm">Carregando slots…</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {items.map((it) => (
            <SlotCard
              key={it.key}
              slot={it}
              bust={bust}
              busy={busy === it.key}
              onUpload={(f) => handleUpload(it.key, f)}
              onRemove={() => handleRemove(it.key)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function SlotCard({ slot, bust, busy, onUpload, onRemove }) {
  const inputRef = useRef(null);
  const preview = slot.url ? (slot.url.startsWith("/api") ? `${slot.url}?v=${bust}` : slot.url) : "";
  return (
    <div className="bg-white rounded-2xl border overflow-hidden" style={{ borderColor: BORDER }} data-testid={`slot-${slot.key}`}>
      <div className="aspect-[16/9] relative bg-[#F4F2EF] flex items-center justify-center overflow-hidden">
        {preview ? (
          <img src={preview} alt={slot.label} className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <div className="text-slate-400 text-sm flex flex-col items-center gap-2">
            <ImageIcon className="w-6 h-6" /> sem imagem
          </div>
        )}
        {slot.custom && (
          <span className="absolute top-2 right-2 text-[10px] uppercase font-bold px-2 py-0.5 rounded-full bg-emerald-600 text-white shadow">
            customizada
          </span>
        )}
      </div>

      <div className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">{slot.key}</div>
            <div className="font-serif text-lg leading-tight mt-1">{slot.label}</div>
          </div>
          <a href={preview || "#"} target="_blank" rel="noreferrer"
             className="text-slate-400 hover:text-[#132238]" title="Abrir em nova aba">
            <ExternalLink className="w-4 h-4"/>
          </a>
        </div>

        <div className="mt-3 space-y-1.5 text-[12px] text-slate-600">
          <div className="flex items-start gap-1.5"><Info className="w-3 h-3 mt-0.5" style={{ color: GOLD }}/> <span><strong>Onde aparece:</strong> {slot.location}</span></div>
          <div className="flex items-start gap-1.5"><ImageIcon className="w-3 h-3 mt-0.5" style={{ color: GOLD }}/> <span><strong>Formato recomendado:</strong> {slot.recommended}</span></div>
          {slot.updated_at && (
            <div className="text-[11px] text-slate-400">Atualizada em {new Date(slot.updated_at).toLocaleString("pt-BR")}</div>
          )}
        </div>

        <div className="mt-4 flex items-center gap-2">
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            className="hidden"
            data-testid={`slot-${slot.key}-input`}
            onChange={(e) => {
              const f = e.target.files?.[0];
              onUpload(f);
              if (inputRef.current) inputRef.current.value = "";
            }}
          />
          <button
            disabled={busy}
            onClick={() => inputRef.current?.click()}
            className="flex-1 h-9 rounded-lg text-[12px] font-semibold text-white inline-flex items-center justify-center gap-1.5 disabled:opacity-60"
            style={{ background: NAVY }}
            data-testid={`slot-${slot.key}-upload`}
          >
            {busy ? <RefreshCw className="w-3.5 h-3.5 animate-spin"/> : <Upload className="w-3.5 h-3.5"/>}
            {slot.custom ? "Substituir" : "Enviar imagem"}
          </button>
          {slot.custom && (
            <button
              disabled={busy}
              onClick={onRemove}
              className="h-9 px-3 rounded-lg border text-[12px] text-rose-700 hover:bg-rose-50 inline-flex items-center gap-1"
              style={{ borderColor: BORDER }}
              data-testid={`slot-${slot.key}-remove`}
              title="Reverter para o padrão"
            >
              <Trash2 className="w-3.5 h-3.5"/> Reverter
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
